import React, { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import Loading from "../components/Loading";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import UserService from "../services/UserService";
import Select from "react-select";
import DataTable from "react-data-table-component";
import {CSVLink} from "react-csv";
import dayjs from "dayjs";
import DatePicker from 'react-date-picker';
import 'react-date-picker/dist/DatePicker.css';
import '../styles/components/admin-lane-data-search.css';

export const AdminLaneDataSearch = () => {
    // get functions to build form with useForm() hook
    const { register, handleSubmit, reset } = useForm();
    const financial = (x) => Number.parseFloat(x).toFixed(2);
    
    // user state for form
    const [farelanesUser, setFarelanesUser] = useState(null);

    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());

    const [factorProfileOptions, setFactorProfileOptions] = useState([]);
    const [selectedDataSource, setSelectedDataSource] = useState(null);
    
    const columns = [
        {
            name: 'Pick City',
            selector: row => row.key.pickCity + ', ' + row.key.pickState,
            sortable: true,
            width: '250px'
        },
        {
            name: 'Drop City',
            selector: row => row.key.endCity + ', ' + row.key.endState,
            sortable: true,
            width: '250px'
        },
        {
            id: 'pairs',
            name: '# Pairs',
            selector: row => row.recordPairs,
            descending: true,
            sortable: true,
            width: '150px'
        },
        {
            id: 'lane',
            name: 'Total Line Haul Rate',
            selector: row => '$' + financial(row.totalLaneAmount),
            sortable: true,
            width: '200px'
        },
        {
            id: 'lane',
            name: 'Average Line Haul Rate',
            selector: row => '$' + financial(row.totalLaneAmount / row.recordPairs),
            sortable: true,
            width: '220px'
        }
    ];

    function handleDataSourceChange(value) {
        setSelectedDataSource(value);
    }

    async function handleSearch() {
        const token = await getAccessTokenSilently();

        const text = selectedDataSource ? selectedDataSource.value : '';

        setState({
            message: "loading..",
            loading: true
        });

        const formatDate = (d) => {
            if (!d) return null;
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };
        const startStr = formatDate(startDate);
        const endStr = formatDate(endDate);

        UserService.doGeneralDataBigSearch(startStr, endStr, text, token).then(
            response => {
                setData(response.data);
                setState({
                    loading: false,
                    message: 'Success!'
                });
                if (response.data.statistic.length == 0) {
                    setState({
                        message: 'No records found!',
                    });
                }
            },
            error => {
                if (error.response && error.response.status === 401) {
                }
            }
        );
    }

    const [data, setData] = useState(new Date());
    const [state, setState] = useState({
        showResult: false,
        apiMessage: 'Tickers are loading ....',
        error: null,
        content: '',
        message: '',
    });

    const {
        getAccessTokenSilently,
        user
    } = useAuth0();
    
    const callApi = async () => {
        try {
            const token = await getAccessTokenSilently();
            UserService.me(token, user).then(
                response => {
                    setState({
                        ...state,
                        showResult: true,
                        apiMessage: response.data
                    });
                    setFarelanesUser({ firstName: response.data.auth0GivenName, lastName: response.data.auth0FamilyName })
                }
            );
        } catch (error) {
            setState({
                ...state,
                error: error.error,
            });
        }
    };

    const fetchFactorProfiles = async () => {
        try {
            const token = await getAccessTokenSilently();
            const response = await UserService.getFactorProfiles(token);
            const options = (response.data || []).map(fp => ({
                value: fp.collectionNameUpdated || fp.collectionName || fp.id,
                label: fp.companyName || 'Unknown'
            }));
            setFactorProfileOptions(options);
        } catch (error) {
            console.error('Failed to fetch factor profiles:', error);
            setFactorProfileOptions([]);
        }
    };

    // effect runs on component mount
    useEffect(() => {
        callApi().then();
        fetchFactorProfiles();
    }, []);

    // effect runs when user state is updated
    useEffect(() => {
        reset(farelanesUser);
    }, [farelanesUser]);

    function onSubmit(data) {
        return false;
    }

    // Custom table styles - vibrant, modern look with multi-color rows
    const customStyles = {
        table: {
            style: {
                backgroundColor: 'transparent',
                overflow: 'hidden',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 104, 56, 0.12)',
                fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif"
            }
        },
        headRow: {
            style: {
                background: 'linear-gradient(135deg, #006838 0%, #00894d 100%)',
                minHeight: '56px',
                borderRadius: '12px 12px 0 0'
            }
        },
        headCells: {
            style: {
                padding: '16px 20px',
                fontSize: '15px',
                fontWeight: '700',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: 'center',
                borderRight: '1px solid rgba(255,255,255,0.2)'
            }
        },
        rows: {
            style: {
                minHeight: '56px',
                fontSize: '15px',
                transition: 'background-color 0.15s ease'
            }
        },
        cells: {
            style: {
                padding: '16px 20px',
                textAlign: 'center',
                verticalAlign: 'middle',
                borderRight: '1px solid #e8f5e9',
                fontWeight: '500'
            }
        }
    };

    const conditionalRowStyles = [
        {
            when: row => true,
            style: (row, index) => ({
                backgroundColor: index % 4 === 0 ? '#ffffff' : 
                               index % 4 === 1 ? '#e8f5e9' : 
                               index % 4 === 2 ? '#f1f8e9' : '#e3f2fd'
            })
        }
    ];

    return (
        <div className="admin-lane-search-container">
            <div className="bg-image">
                {!farelanesUser && (
                    <div className="text-center p-3">
                        <Loading />
                    </div>
                )}

                {farelanesUser && (
                    <div className="container-fluid">

                        {/*<div style={{ marginTop: "20px", marginBottom: "15px" }}>*/}
						<p className="lane-data-intro mb-4">
                        	Search and analyze lane data pairs by date range. Select your data sources, specify the date range, and view detailed statistics including pick/drop cities, number of pairs, and line haul rates.
                        </p>
                        {/*</div>*/}

                        {/* Search Form */}
                        <div className="lane-data-search-box row align-items-end">

                            <div className="col-3 col-md-3 lane-data-source-col">
                                <label><b>Data Source</b></label>
                                <Select
                                    isMulti={false}
                                    isSearchable={true}
                                    options={factorProfileOptions}
                                    value={selectedDataSource}
                                    onChange={handleDataSourceChange}
                                    placeholder="Select data source..."
                                    className="custom-select-dropdown"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '8px',
                                            border: '1px solid #dee2e6',
                                            minHeight: '38px',
                                            minWidth: '280px',
                                            boxShadow: 'none'
                                        })
                                    }}
                                />
                            </div>
                            <div className="col-3 filter-group">
                                <label className="filter-label pick">Start Date</label>
                                <DatePicker 
                            		className="date-picker start-date-picker"
                                    value={startDate} 
                                    onChange={(date) => setStartDate(date)}
                                    clearIcon={null}
                                    calendarIcon={null}
                                />
                            </div>
                            <div className="col-3 filter-group">
                                <label className="filter-label drop">End Date</label>
                                <DatePicker 
                            		className="date-picker end-date-picker"
                                    value={endDate} 
                                    onChange={(date) => setEndDate(date)}
                                    clearIcon={null}
                                    calendarIcon={null}
                                />
                            </div>
                            <div className="col-3 col-md-3 lane-data-search-btn-col">
                                <button className="query-button" onClick={handleSearch} disabled={state.loading}>
                                    {state.loading ? (
										<span className="spinner-border spinner-border-sm"></span>
                               		) : (
                                		<span>Search</span>
	                                )}   
                                </button>
                            </div>
                        </div>

                        {/* Results */}
                        {data && data.statistic && data.statistic.length > 0 && (
                            <div className="lane-data-results">
                                <header className="top-routes-text">
                                    <DataTable
                                        customStyles={customStyles}
                                        conditionalRowStyles={conditionalRowStyles}
                                        striped={false}
                                        columns={columns}
                                        data={data.statistic}
                                        defaultSortFieldId="pairs"
                                        direction="desc"
                                        fixedHeaderScrollHeight="450px"
                                        pagination
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                        responsive
                                        subHeaderAlign="right"
                                        subHeaderWrap
                                        defaultSortAsc={false}
                                        className="lane-data-table"
                                    />
                                </header>
                                
                                <div className="lane-data-export">
                                    <CSVLink 
                                        data={data.statistic} 
                                        filename={`lane-data-${dayjs().format('YYYY-MM-DD')}.csv`}
                                        className="btn btn-export-csv"
                                    >
                                        Export to CSV
                                    </CSVLink>
                                </div>
                            </div>
                        )}

                        {/* No Data Message */}
                        {data && data.statistic && data.statistic.length === 0 && (
                            <div className="text-center p-5">
                                <h4>No Data Found</h4>
                                <p className="text-muted">
                                    {state.message || 'Try adjusting your search criteria or date range'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default withAuthenticationRequired(AdminLaneDataSearch, {
    onRedirecting: () => <Loading />,
});
